import React from 'react'
import { CsCloseIcon } from '../icons'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { css, cx } from '@emotion/css'
import { Modal } from '.'
import { drilldownHeaderCss, metadataCss } from '../text'
import { Button } from '@contrast-security-inc/design-system-buttons'

// Testing hooks
export const cancelIconTestID = 'cancel-icon'

export type DialogSize = 'small' | 'medium' | 'large' | 'x-large'

export interface DialogContainerProps {
  size: DialogSize
  children: React.ReactNode
  testId?: string
  style?: React.CSSProperties
}

const containerCss = css`
  display: flex;
  flex-direction: column;
  pointer-events: auto;
  background-color: ${theme.color.white};
  border-radius: 4px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
`

const sizeCss = css`
  min-width: 275px;
`

const sizeToClassName: Record<DialogSize, string> = {
  small: cx(
    containerCss,
    css`
      ${sizeCss};
      width: 30%;
    `
  ),
  medium: cx(
    containerCss,
    css`
      ${sizeCss};
      width: 40%;
    `
  ),
  large: cx(
    containerCss,
    css`
      ${sizeCss};
      width: 60%;
    `
  ),
  'x-large': cx(
    containerCss,
    css`
      ${sizeCss};
      width: 80%;
    `
  )
}

export function DialogContainer({
  size,
  children,
  testId,
  style
}: DialogContainerProps) {
  return (
    <div
      style={style}
      className={cx('legacy-ds-dialog-container', sizeToClassName[size])}
      data-testid={testId}
      data-e2e={testId}
    >
      {children}
    </div>
  )
}

export interface HeaderProps {
  onCancel: () => void
  headerContainerClassName?: string
  closeIconClassName?: string
  children: React.ReactNode
  closeIconSize?: number
}

const headerContainerCss = css`
  display: flex;
  align-items: center;
  background-color: ${theme.color.neutral10};
  padding: 18px 20px;
  border-radius: 4px 4px 0 0;
  border-bottom: 1px solid ${theme.color.neutral30};
`

const titleContainerCss = css`
  flex: 1 1 auto;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  word-wrap: normal;
`

const closeIconCss = css`
  margin-left: 8px;
`

export function DialogHeader({
  onCancel,
  headerContainerClassName,
  closeIconClassName,
  children,
  closeIconSize = 12
}: HeaderProps) {
  return (
    <div
      className={cx(
        'legacy-ds-dialog-container-header',
        headerContainerCss,
        headerContainerClassName
      )}
    >
      <div className={titleContainerCss}>{children}</div>
      <CsCloseIcon
        onClick={onCancel}
        size={closeIconSize}
        className={cx(closeIconCss, closeIconClassName)}
        data-testid={cancelIconTestID}
      />
    </div>
  )
}

export interface BodyProps {
  children: React.ReactNode
  className?: string
}

const bodyContainerCss = css`
  padding: 20px;
`

export function DialogBody({ children, className }: BodyProps) {
  return <div className={cx(bodyContainerCss, className)}>{children}</div>
}

const footerContainerCss = css`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  padding: 10px 20px 20px 20px;
`

export const footerButtonCss = css`
  margin-left: 8px;
`

export interface FooterProps {
  extraChildren?: React.ReactNode
  children?: React.ReactNode
}

export function DialogFooter({ extraChildren, children }: FooterProps) {
  return (
    <div className={footerContainerCss}>
      <div
        className={css`
          display: flex;
          align-items: center;
        `}
      >
        {extraChildren}
      </div>
      <div
        className={css`
          display: flex;
          margin-left: auto;
        `}
      >
        {children}
      </div>
    </div>
  )
}

export interface FooterButtonProps {
  renderMessage: () => React.ReactNode
  onClick: () => void
  disabled?: boolean
}

export interface DialogProps {
  /**
   * Whether or not the Dialog should be shown.
   */
  isOpen: boolean

  /**
   * Callback that will be invoked when a cancel event occurs, such as
   * the close icon or being clicked or the escape button being pressed.
   *
   * Note: This component is stateless, so it is left up to the
   * user of this component to handle the cancel event
   */
  onCancel: () => void

  /**
   * Render prop for the title content, typically some text
   */
  renderTitle: () => React.ReactNode

  /**
   * Render prop for the description content, typically some text
   */
  renderDescription?: () => React.ReactNode

  /**
   * Optional prop for the width of the dialog
   */
  size?: DialogSize

  children: React.ReactNode

  testId?: string

  className?: string
}

export function Dialog({
  isOpen,
  onCancel,
  renderTitle,
  renderDescription,
  children,
  size = 'medium',
  testId,
  className
}: DialogProps) {
  return (
    <Modal isOpen={isOpen} onRequestClose={onCancel} className={className}>
      <DialogContainer size={size} testId={testId}>
        <DialogHeader onCancel={onCancel}>
          <div className={drilldownHeaderCss}>{renderTitle()}</div>
          {renderDescription && (
            <div className={metadataCss}>{renderDescription()}</div>
          )}
        </DialogHeader>
        {children}
      </DialogContainer>
    </Modal>
  )
}

interface BasicDialogBodyProps {
  body: React.ReactNode
  confirmButtonText: React.ReactNode
  cancelButtonText: React.ReactNode
  footerChildren?: React.ReactNode
  onCancel: () => void
  onConfirm: () => void
  disableConfirmButton?: boolean
  hideConfirmButton?: boolean
  hideCancelButton?: boolean
}

export function BasicDialogBody({
  body,
  confirmButtonText,
  cancelButtonText,
  onCancel,
  onConfirm,
  footerChildren,
  disableConfirmButton = false,
  hideConfirmButton = false,
  hideCancelButton = false
}: BasicDialogBodyProps) {
  return (
    <React.Fragment>
      <DialogBody>{body}</DialogBody>
      <DialogFooter extraChildren={footerChildren}>
        {hideCancelButton ? undefined : (
          <Button
            variant="secondary"
            className={footerButtonCss}
            onClick={onCancel}
          >
            {cancelButtonText}
          </Button>
        )}
        {hideConfirmButton ? undefined : (
          <Button
            className={footerButtonCss}
            onClick={onConfirm}
            autoFocus
            isDisabled={disableConfirmButton}
          >
            {confirmButtonText}
          </Button>
        )}
      </DialogFooter>
    </React.Fragment>
  )
}

interface BasicDialogBodyWithoutCancelProps {
  body: React.ReactNode
  confirmButtonText: React.ReactNode
  footerChildren?: React.ReactNode
  onConfirm: () => void
}

export function BasicDialogBodyWithoutCancel({
  body,
  confirmButtonText,
  onConfirm,
  footerChildren
}: BasicDialogBodyWithoutCancelProps) {
  return (
    <React.Fragment>
      <DialogBody>{body}</DialogBody>
      <DialogFooter extraChildren={footerChildren}>
        <Button className={footerButtonCss} onClick={onConfirm} autoFocus>
          {confirmButtonText}
        </Button>
      </DialogFooter>
    </React.Fragment>
  )
}
